import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, MaintenanceModeConfig, MaintenancePlatformConfig } from "../api/client";
import { useConfirm } from "../components/ConfirmDialog";

const SETTING_KEY = "maintenance_mode";
const VERSION_RE = /^\d+\.\d+\.\d+$/;

type Platform = "ios" | "android";

const EMPTY: MaintenanceModeConfig = {
  ios: { enabled: false, versions: [] },
  android: { enabled: false, versions: [] },
};

function isValidVersion(v: string): boolean {
  return VERSION_RE.test(v.trim());
}

function sanitizeVersion(raw: string): string {
  const digitsAndDots = raw.replace(/[^\d.]/g, "");
  const parts = digitsAndDots.split(".").slice(0, 3);
  return parts.map((p) => p.slice(0, 4)).join(".");
}

function equalConfig(a: MaintenanceModeConfig, b: MaintenanceModeConfig): boolean {
  return (
    a.ios.enabled === b.ios.enabled &&
    a.android.enabled === b.android.enabled &&
    a.ios.versions.join(",") === b.ios.versions.join(",") &&
    a.android.versions.join(",") === b.android.versions.join(",")
  );
}

export default function MaintenanceModePage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const [saved, setSaved] = useState<MaintenanceModeConfig>(EMPTY);
  const [current, setCurrent] = useState<MaintenanceModeConfig>(EMPTY);

  const flashTimer = useRef<number | null>(null);
  const { confirm, dialog } = useConfirm();

  const load = useCallback(async () => {
    try {
      const cfg = await api.getMaintenanceMode();
      const normalized: MaintenanceModeConfig = {
        ios: {
          enabled: !!cfg.ios?.enabled,
          versions: Array.isArray(cfg.ios?.versions) ? [...cfg.ios.versions] : [],
        },
        android: {
          enabled: !!cfg.android?.enabled,
          versions: Array.isArray(cfg.android?.versions) ? [...cfg.android.versions] : [],
        },
      };
      setSaved(normalized);
      setCurrent(normalized);
    } catch (err) {
      console.error("Failed to load maintenance mode", err);
      setError("Не удалось загрузить настройки");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
    };
  }, []);

  const dirty = !equalConfig(current, saved);
  const canSave = dirty && !saving;

  const handleSave = useCallback(async () => {
    // Confirm only when flipping at least one platform from off → on; silent
    // save is fine for version-list edits or toggling off.
    const isNewlyEnabling =
      (current.ios.enabled && !saved.ios.enabled) ||
      (current.android.enabled && !saved.android.enabled);

    if (isNewlyEnabling) {
      const affected: string[] = [];
      if (current.ios.enabled) {
        affected.push(
          `iOS: ${current.ios.versions.length === 0 ? "все версии" : current.ios.versions.join(", ")}`
        );
      }
      if (current.android.enabled) {
        affected.push(
          `Android: ${current.android.versions.length === 0 ? "все версии" : current.android.versions.join(", ")}`
        );
      }
      const confirmed = await confirm({
        title: "Включить техработы?",
        body: (
          <>
            <p>
              Пользователи этих версий увидят экран «идут работы» и не смогут
              пользоваться приложением, пока режим не выключат.
            </p>
            {affected.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </>
        ),
        confirmLabel: "Включить техработы",
        danger: true,
      });
      if (!confirmed) return;
    }

    setSaving(true);
    setError(null);
    setSavedFlash(false);
    try {
      await api.upsertSetting(SETTING_KEY, JSON.stringify(current));
      setSaved(current);
      setSavedFlash(true);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setSavedFlash(false), 2500);
    } catch (err: any) {
      console.error("Failed to save maintenance mode", err);
      setError(err?.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  }, [current, saved, confirm]);

  const handleReset = () => {
    setCurrent(saved);
    setError(null);
  };

  const handleDisableAll = () => {
    setCurrent((c) => ({
      ios: { ...c.ios, enabled: false },
      android: { ...c.android, enabled: false },
    }));
    setError(null);
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        if (canSave) handleSave();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [canSave, handleSave]);

  const updatePlatform = useCallback(
    (platform: Platform, patch: Partial<MaintenancePlatformConfig>) => {
      setCurrent((c) => ({ ...c, [platform]: { ...c[platform], ...patch } }));
    },
    []
  );

  const cards = useMemo(
    () => [
      {
        platform: "ios" as Platform,
        title: "iOS",
        subtitle: "Показывать экран техподдержки для выбранных версий iOS.",
      },
      {
        platform: "android" as Platform,
        title: "Android",
        subtitle: "Показывать экран техподдержки для выбранных версий Android.",
      },
    ],
    []
  );

  return (
    <div className="page mm-page">
      <div className="mav-header">
        <div>
          <h2>Техработы</h2>
          <p className="page-description">
            Когда режим включён, пользователи указанных версий видят полноэкранный
            блокирующий экран «идут работы» при старте приложения. Точное совпадение
            версии — можно указать несколько. Пустой список при включённом флаге
            означает <strong>все версии</strong> платформы.
          </p>
        </div>
        <button
          className="mav-link-btn"
          onClick={handleDisableAll}
          disabled={saving || (!current.ios.enabled && !current.android.enabled)}
          title="Выключить оба переключателя"
        >
          Выключить обе
        </button>
      </div>

      <div className="card-list">
        {loading
          ? [0, 1].map((i) => <SkeletonCard key={i} />)
          : cards.map((c) => (
              <MaintenanceCard
                key={c.platform}
                title={c.title}
                subtitle={c.subtitle}
                config={current[c.platform]}
                savedConfig={saved[c.platform]}
                disabled={saving}
                onChange={(patch) => updatePlatform(c.platform, patch)}
              />
            ))}
      </div>

      <div className="mav-actions">
        <button
          className="mav-btn-primary"
          onClick={handleSave}
          disabled={!canSave}
          title="⌘S"
        >
          {saving ? "Сохраняем…" : "Сохранить"}
        </button>
        <button
          className="mav-btn-secondary"
          onClick={handleReset}
          disabled={!dirty || saving}
        >
          Сбросить
        </button>

        <div className="mav-status" aria-live="polite">
          {error ? (
            <span className="mav-status-error">{error}</span>
          ) : savedFlash ? (
            <span className="mav-status-ok">Сохранено ✓</span>
          ) : dirty ? (
            <span className="mav-status-dirty">Есть несохранённые изменения</span>
          ) : (
            <span className="mav-status-muted">Всё сохранено</span>
          )}
        </div>
      </div>

      {dialog}
    </div>
  );
}

interface MaintenanceCardProps {
  title: string;
  subtitle: string;
  config: MaintenancePlatformConfig;
  savedConfig: MaintenancePlatformConfig;
  disabled: boolean;
  onChange: (patch: Partial<MaintenancePlatformConfig>) => void;
}

function MaintenanceCard({
  title,
  subtitle,
  config,
  savedConfig,
  disabled,
  onChange,
}: MaintenanceCardProps) {
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState<string | null>(null);

  const isDirty =
    config.enabled !== savedConfig.enabled ||
    config.versions.join(",") !== savedConfig.versions.join(",");

  const addVersion = () => {
    const v = sanitizeVersion(draft.trim());
    if (!isValidVersion(v)) {
      setDraftError("Формат: major.minor.patch (например 2.0.2)");
      return;
    }
    if (config.versions.includes(v)) {
      setDraftError("Эта версия уже добавлена");
      return;
    }
    onChange({ versions: [...config.versions, v] });
    setDraft("");
    setDraftError(null);
  };

  const removeVersion = (v: string) => {
    onChange({ versions: config.versions.filter((x) => x !== v) });
  };

  return (
    <div className={`mav-card ${isDirty ? "is-dirty" : ""}`}>
      <div className="mav-card-info">
        <div className="mav-card-title-row">
          <h3>{title}</h3>
          {config.enabled ? (
            <span className="mav-badge mav-badge-active">
              {config.versions.length === 0
                ? "Все версии"
                : `${config.versions.length} верс.`}
            </span>
          ) : (
            <span className="mav-badge mav-badge-muted">Выключено</span>
          )}
          {isDirty && <span className="mav-badge mav-badge-dirty">Изменено</span>}
        </div>
        <p>{subtitle}</p>
      </div>

      <div className="mav-card-input mm-card-body">
        <div className="mm-versions">
          <label className="mav-input-label">Версии</label>
          {config.versions.length === 0 ? (
            <div className="mm-empty">
              {config.enabled
                ? "Список пуст — под блокировку попадут все версии"
                : "Версии можно добавить заранее. Блок включится, когда переключатель ниже активен."}
            </div>
          ) : (
            <div className="mm-chip-row">
              {config.versions.map((v) => (
                <span key={v} className="mm-chip">
                  {v}
                  <button
                    type="button"
                    className="mm-chip-x"
                    disabled={disabled}
                    onClick={() => removeVersion(v)}
                    aria-label={`Удалить ${v}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="mm-add-row">
            <input
              className={`mav-input ${draftError ? "has-error" : ""}`}
              value={draft}
              onChange={(e) => {
                setDraft(sanitizeVersion(e.target.value));
                if (draftError) setDraftError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addVersion();
                }
              }}
              placeholder="2.0.2"
              inputMode="decimal"
              autoComplete="off"
              spellCheck={false}
              disabled={disabled}
            />
            <button
              type="button"
              className="mav-btn-secondary mm-add-btn"
              onClick={addVersion}
              disabled={disabled || draft.trim().length === 0}
            >
              Добавить
            </button>
          </div>
          {draftError && <span className="mav-hint is-error">{draftError}</span>}
        </div>

        <label className="mm-toggle">
          <input
            type="checkbox"
            checked={config.enabled}
            disabled={disabled}
            onChange={(e) => onChange({ enabled: e.target.checked })}
          />
          <span>Включить режим техподдержки</span>
        </label>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="mav-card mav-card-skeleton">
      <div className="mav-card-info">
        <div className="skeleton-line skeleton-line-title" />
        <div className="skeleton-line skeleton-line-sub" />
      </div>
      <div className="mav-card-input">
        <div className="skeleton-line skeleton-line-label" />
        <div className="skeleton-line skeleton-line-input" />
      </div>
    </div>
  );
}
